import type { Board } from 'common/api/endpoints/boards';
import type { StrippedUser } from 'common/api/endpoints/users';
import type {
  IdeaCreateAction,
  IdeaDismissAction,
  IdeaMergeAction,
  InsightCreateAction,
  PostAction,
  PostDraftCreateAction,
  PostDraftDismissAction,
  PostDraftMergeAction,
} from 'common/api/resources/autopilotActions';
import type { Translation } from 'common/api/resources/i18n';
import type { PostDraft } from 'common/api/resources/postDrafts';
import type { Post } from 'common/api/resources/posts';
import type { SyncIntegrationNames } from 'common/constants/autopilotIntegrations';
import type { Source } from 'common/constants/sources';

// types related to the output of GetQueueItemsEndpoint and the types used in QueueSuggestedCell etc
export enum QueueItemType {
  post = 'post',
  draft = 'draft',
}

export type WithAuthor<T> = T & { author: StrippedUser };
type WithBoard<T> = T & { board: Board };
export type WithUsers<T> = T & { users: StrippedUser[] };

export type QueuePost = Post & {
  type: QueueItemType.post;
  source: 'canny';
};

export type QueuePostDraft = PostDraft & {
  type: QueueItemType.draft;
  source: SyncIntegrationNames;
  link: string | null;
};

export type QueueItem = WithAuthor<QueuePost | QueuePostDraft>;

// types related to the output of GetAutopilotActionItemsEndpoint and the types used in AuditLogSuggestionCell etc
// to optimise performance, we only return the fields needed for the UI - hence the StrippedPost and StrippedPostDraft types

export type ActionFields =
  | '_id'
  | 'actionType'
  | 'boardID'
  | 'createdAt'
  | 'createdPostID'
  | 'duplicatePostID'
  | 'sourceID'
  | 'sourceType'
  | 'targetIdeaID';

export type StrippedPost = WithBoard<{
  // board is added by AutopilotActionItemListContainer and is not returned by the API
  _id: string;
  authorID: string | null;
  boardID: string;
  commentCount: number;
  created?: string; // ISO string
  details: string;
  score: number;
  title: string;
  totalMRR: number;
  urlName: string;
}>;

export type StrippedPostDraft = {
  _id: string;
  authorID: string;
  createdAt: string;
  details: string | null;
  featureExtractionItemID: string;
  title: string;
  workaround: string | null;
};

export type StrippedIdea = {
  _id: string;
  created: string;
  description: string;
  title: string;
  urlName: string;
};

export type StrippedInsight = {
  _id: string;
  created: string;
  value: string | null;
  url: string | null;
};

export type PostActionItem = Pick<PostAction, ActionFields> & {
  board?: Board;
  duplicatePost: WithAuthor<StrippedPost>;
  source: 'canny';
  sourcePost: WithAuthor<StrippedPost> & { translation?: Translation<StrippedPostDraft> };
  urlName: string;
};

type PostDraftCommon = {
  board?: Board;
  source: SyncIntegrationNames;
  sourcePostDraft: WithAuthor<StrippedPostDraft> & { translation?: Translation<StrippedPostDraft> };
  link: string | null;
};

type PostDraftMergeActionItem = Pick<PostDraftMergeAction, ActionFields> &
  PostDraftCommon & {
    createdPost: null;
    duplicatePost: WithAuthor<StrippedPost>;
  };

type PostDraftCreateActionItem = Pick<PostDraftCreateAction, ActionFields> &
  PostDraftCommon & {
    createdPost: WithAuthor<StrippedPost>;
    duplicatePost: null;
  };

type PostDraftDismissActionItem = Pick<PostDraftDismissAction, ActionFields> &
  PostDraftCommon & {
    createdPost: null; // actions are dismissed before any posts are created
    duplicatePost: null | WithAuthor<StrippedPost>;
  };

export type PostDraftActionItem =
  | PostDraftMergeActionItem
  | PostDraftCreateActionItem
  | PostDraftDismissActionItem;

export type ActionItem =
  | IdeaActionItem
  | InsightActionItem
  | PostActionItem
  | PostDraftMergeActionItem
  | PostDraftCreateActionItem
  | PostDraftDismissActionItem;

type IdeaCommon = {
  source: Source;
};

type IdeaCreateActionItem = Pick<IdeaCreateAction, ActionFields> &
  IdeaCommon & {
    createdIdea: WithAuthor<StrippedIdea>;
    targetIdea: null;
  };

type IdeaMergeActionItem = Pick<IdeaMergeAction, ActionFields> &
  IdeaCommon & {
    createdIdea: WithAuthor<StrippedIdea>;
    targetIdea: WithAuthor<StrippedIdea>;
  };

type IdeaDismissActionItem = Pick<IdeaDismissAction, ActionFields> &
  IdeaCommon & {
    createdIdea: WithAuthor<StrippedIdea>;
    targetIdea: null;
  };

export type IdeaActionItem = IdeaCreateActionItem | IdeaMergeActionItem | IdeaDismissActionItem;

type InsightCommon = {
  source: Source;
};

type InsightCreateActionItem = Pick<InsightCreateAction, ActionFields> &
  InsightCommon & {
    sourceInsight: WithUsers<WithAuthor<StrippedInsight>>;
    targetIdea: WithAuthor<StrippedIdea>;
  };

export type InsightActionItem = InsightCreateActionItem;
